import { DOCUMENT } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  inject,
  Input,
  model,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Consumer, Device, Producer, Transport, TransportOptions } from 'mediasoup-client/types';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  from,
  fromEvent,
  interval,
  lastValueFrom,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import { FileStorageApiService } from '@app/api/file-storage/file-storage-api.service';
import { DeviceStreamApiService } from '@app/api/stream/device-stream-api.service';
import { DeviceStreamEventsService } from '@app/api/stream/device-stream-events.service';
import { RoomMode } from '@app/api/stream/dtos/stream-create-room.request.dto';
import { IconButtonComponent } from '@app/shared/components/buttons/icon-button/icon-button.component';
import { canvasToBlob } from '@app/shared/utils/canvas.utils';
import { isFullscreen, isFullscreenSupported, toggleFullscreen } from '@app/shared/utils/fullscreen.utils';
import { createRecord } from '@app/shared/utils/record.utils';

import { DeviceNamePipe } from '../pipes/device-name.pipe';

@Component({
  selector: 'app-device-call',
  imports: [MatProgressSpinner, MatIcon, IconButtonComponent, DeviceNamePipe],
  templateUrl: './device-call.component.html',
  styleUrls: ['./device-call.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceCallComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  public get deviceId(): string {
    return this._deviceId;
  }

  public set deviceId(val: string) {
    if (val !== this._deviceId) {
      this.disconnect();
      this._deviceId = val;
      this.connect();
    }
  }

  @Input()
  public deviceName?: string | null;

  @Input({ transform: booleanAttribute })
  public set muted(val: boolean) {
    this.muted$.next(val);
  }

  public get muted(): boolean {
    return this.muted$.value;
  }

  @Input({ transform: booleanAttribute }) responsive = false;

  @Output() closeClicked = new EventEmitter<void>();

  public volume = model(1);

  public volumePercentStyle = computed(() => {
    return `${this.volume() * 100}%`;
  });

  protected rotation = signal(0);

  protected rotationTransform = computed(() => {
    const rotation = this.rotation();
    const scale = rotation === 90 || rotation === 270 ? 0.75 : 1;
    return `rotate(${rotation}deg) scale(${scale})`;
  });

  protected isFullscreen$ = new BehaviorSubject<boolean>(false);

  protected isFullscreenSupported = isFullscreenSupported();

  protected uploading = signal(false);

  protected instanceDisconnected$ = new Subject<void>();

  protected muted$ = new BehaviorSubject<boolean>(false);

  protected videoElRef = viewChild<ElementRef<HTMLVideoElement>>('videoEl');

  protected audioElRef = viewChild<ElementRef<HTMLAudioElement>>('audioEl');

  protected recvTransport?: Transport;

  protected sendTransport?: Transport;

  protected _deviceId!: string;

  protected readonly $consumerLoading = signal(true);

  protected readonly $producerLoading = signal(true);

  protected readonly $loading = computed(() => this.$consumerLoading() || this.$producerLoading());

  protected error = signal(false);

  protected audioConsumer?: Consumer;

  protected videoConsumer?: Consumer;

  protected audioProducer?: Producer;

  protected videoProducer?: Producer;

  protected mediaRecorder?: MediaRecorder;

  protected recordActive = signal(false);

  protected recordStartTime = 0;

  protected recordTimer = signal(0);

  protected recordTimeLabel = computed(() => {
    const timer = this.recordTimer();
    const m = Math.floor(timer / 60);
    const s = timer - m * 60;
    const d = (v: number) => (v >= 10 ? `${v}` : `0${v}`);
    return `${d(m)}:${d(s)}`;
  });

  protected showControls = signal(false);

  protected hideControlsTimeout: number | null = null;

  protected controlsHover = false;

  protected recordFinished$ = new Subject<void>();

  protected roomId: string | null = null;

  private readonly disconnected$ = new Subject<void>();

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly deviceStreamApi: DeviceStreamApiService,
    private readonly deviceStreamEvents: DeviceStreamEventsService,
    private readonly fileStorageApi: FileStorageApiService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public ngOnInit(): void {
    this.deviceStreamEvents.roomClosed$
      .pipe(
        filter((payload) => payload.roomId === this.roomId),
        tap(() => {
          this.roomId = null;
          this.disconnect();
          this.$producerLoading.set(false);
          this.$consumerLoading.set(false);
          this.error.set(true);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.disconnect();
    this.clearHideControlsTimer();

    this.disconnected$.complete();
  }

  @HostListener('mousemove')
  public handleMousemove(): void {
    if (!this.controlsHover) {
      this.showControls.set(true);
      this.startHideControlsTimer();
    }
  }

  public handleControlsMouseenter(): void {
    this.controlsHover = true;
    this.showControls.set(true);
    this.clearHideControlsTimer();
  }

  public handleControlsMouseleave(): void {
    this.controlsHover = false;
    this.startHideControlsTimer();
  }

  public startHideControlsTimer(): void {
    this.clearHideControlsTimer();
    this.hideControlsTimeout = setTimeout(() => {
      this.showControls.set(false);
    }, 5000) as unknown as number;
  }

  public clearHideControlsTimer(): void {
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
      this.hideControlsTimeout = null;
    }
  }

  public toggleFullscreen(element: HTMLElement): void {
    toggleFullscreen(element);
  }

  public rotate(): void {
    this.rotation.update((value) => (value >= 270 ? 0 : value + 90));
  }

  public disconnect(): void {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      delete this.mediaRecorder;
    }

    if (this.audioConsumer) {
      this.audioConsumer.close();
      delete this.audioConsumer;
    }

    if (this.videoConsumer) {
      this.videoConsumer.close();
      delete this.videoConsumer;
    }

    if (this.audioProducer) {
      delete this.audioProducer;
    }

    if (this.videoProducer) {
      delete this.videoProducer;
    }

    if (this.recvTransport) {
      this.recvTransport.close();
      delete this.recvTransport;
    }

    if (this.sendTransport) {
      this.sendTransport.close();
      delete this.sendTransport;
    }

    if (this.roomId) {
      this.deviceStreamApi
        .closeRoom(this.roomId)
        .pipe(
          take(1),
          catchError(() => EMPTY),
        )
        .subscribe();
    }

    this.disconnected$.next();
  }

  public connect(): void {
    const deviceId = this.deviceId;
    this.$consumerLoading.set(true);
    this.$producerLoading.set(true);
    this.error.set(false);

    const deviceReady$ = new BehaviorSubject<string | null>(null);

    this.deviceStreamEvents.deviceReady$
      .pipe(
        filter((event) => event.deviceId === deviceId),
        take(1),
        tap(() => deviceReady$.next(deviceId)),
        takeUntil(this.disconnected$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.deviceStreamApi
      .createRoom({
        mode: RoomMode.Bidirectional,
        deviceId,
      })
      .pipe(
        take(1),
        filter(Boolean),
        tap((room) => (this.roomId = room.roomId)),
        switchMap((room) => {
          const device = new Device();
          return from(device.load({ routerRtpCapabilities: room.routerRtpCapabilities })).pipe(
            switchMap(() =>
              forkJoin([
                this.setupConsumer(room.roomId, device, deviceReady$),
                this.setupProducer(room.roomId, device, room.isPlain ? room.audioTransportData : room.transportData),
              ]),
            ),
          );
        }),
        catchError((error) => {
          console.error(error);
          this.error.set(true);
          return EMPTY;
        }),
        takeUntil(this.disconnected$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected setupConsumer(
    roomId: string,
    device: Device,
    deviceReady$: Observable<string | null>,
  ): Observable<unknown> {
    return this.deviceStreamApi
      .addConsumer(roomId)
      .pipe(switchMap((transportData) => this.setupConsumerTransport(roomId, device, transportData, deviceReady$)));
  }

  protected setupConsumerTransport(
    roomId: string,
    device: Device,
    transportData: TransportOptions,
    deviceReady$: Observable<string | null>,
  ): Observable<unknown> {
    const transport = device.createRecvTransport(transportData);
    this.recvTransport = transport;

    transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await lastValueFrom(this.deviceStreamApi.connectConsumer({ roomId, dtlsParameters }));
        callback();
      } catch (error) {
        errback(error as Error);
      }
    });

    transport.on('connectionstatechange', (state) => {
      switch (state) {
        case 'connecting':
          console.log('recvTransport connecting');
          break;

        case 'connected':
          console.log('recvTransport connected');
          this.$consumerLoading.set(false);
          this.error.set(false);
          //localVideo.srcObject = stream
          break;

        case 'failed':
        case 'disconnected':
          console.log(`recvTransport ${state}`);
          this.$consumerLoading.set(false);
          this.error.set(true);
          break;

        default:
          console.log(`recvTransport: ${state}`);
          break;
      }
    });

    return deviceReady$.pipe(
      filter(Boolean),
      take(1),
      concatMap((deviceId) => this.consume(roomId, transport, deviceId, device)),
      catchError((error) => {
        this.$consumerLoading.set(false);
        return throwError(() => error);
      }),
    );
  }

  protected consume(roomId: string, transport: Transport, producerId: string, device: Device): Observable<Consumer[]> {
    return forkJoin([
      this.deviceStreamApi
        .consume({ roomId, producerId, rtpCapabilities: device.rtpCapabilities, kind: 'video' })
        .pipe(switchMap((response) => transport.consume(response))),
      this.deviceStreamApi
        .consume({ roomId, producerId, rtpCapabilities: device.rtpCapabilities, kind: 'audio' })
        .pipe(switchMap((response) => transport.consume(response))),
    ]).pipe(tap((consumers) => consumers.forEach((c) => this.setConsumer(c))));
  }

  protected setupProducer(roomId: string, device: Device, transportOptions: TransportOptions): Observable<unknown> {
    const transport = device.createSendTransport(transportOptions);
    this.sendTransport = transport;

    let audioReady = false;
    let videoReady = false;

    transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await lastValueFrom(this.deviceStreamApi.connectProducer({ roomId, dtlsParameters }));
        callback();
      } catch (error) {
        errback(error instanceof Error ? error : new Error('Unknown error'));
      }
    });

    transport.on('produce', async ({ kind, rtpParameters }, callback, errback) => {
      try {
        const { producerId } = await lastValueFrom(
          this.deviceStreamApi.produce({ roomId, kind, rtpParameters, paused: kind === 'video' }),
        );

        callback({ id: producerId });

        switch (kind) {
          case 'audio':
            audioReady = true;
            break;
          case 'video':
            videoReady = true;
            break;
        }

        if (audioReady && videoReady) {
          await lastValueFrom(
            this.deviceStreamApi.producerReady({
              roomId,
              type: 'call',
            }),
          );
        }
      } catch (error) {
        errback(error instanceof Error ? error : new Error('Unknown error'));
      }
    });

    transport.on('connectionstatechange', (state) => {
      switch (state) {
        case 'connecting':
          console.log('sendTransport connecting');
          break;

        case 'connected':
          console.log('sendTransport connected');
          this.$producerLoading.set(false);
          this.error.set(false);
          //localVideo.srcObject = stream
          break;

        case 'failed':
        case 'disconnected':
          console.log(`sendTransport ${state}`);
          this.$producerLoading.set(false);
          this.error.set(true);
          break;

        default:
          console.log(`sendTransport: ${state}`);
          break;
      }
    });

    return from(
      navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      }),
    ).pipe(
      switchMap((stream) =>
        forkJoin([this.produceAudio(transport, stream), this.produceVideo(device, transport, stream)]),
      ),
      catchError((error) => {
        this.$producerLoading.set(false);
        return throwError(() => error);
      }),
    );
  }

  protected setConsumer(consumer: Consumer): void {
    if (consumer.track.kind === 'video') {
      this.videoConsumer = consumer;
      const stream = new MediaStream();
      stream.addTrack(consumer.track);
      const videoEl = this.videoElRef()?.nativeElement;

      if (videoEl) {
        videoEl.srcObject = stream;
        videoEl.play();
        consumer.resume();
      }
    } else {
      this.audioConsumer = consumer;
      const stream = new MediaStream();
      stream.addTrack(consumer.track);
      const audioEl = this.audioElRef()?.nativeElement;

      if (audioEl) {
        audioEl.srcObject = stream;
        audioEl.play();
        consumer.resume();
      }
    }
  }

  protected async produceAudio(transport: Transport, stream: MediaStream): Promise<Producer> {
    const producer = await transport.produce({ track: stream.getAudioTracks()[0], codecOptions: { opusDtx: true } });
    this.audioProducer = producer;
    return producer;
  }

  protected async produceVideo(device: Device, transport: Transport, stream: MediaStream): Promise<Producer> {
    const producer = await transport.produce({
      track: stream.getVideoTracks()[0],
      codec: device.rtpCapabilities.codecs?.find((i) => i.mimeType.toLowerCase() === 'video/vp8'),
    });
    this.videoProducer = producer;
    return producer;
  }

  protected checkIsFullscreen(): void {
    this.isFullscreen$.next(isFullscreen());
  }

  protected setVolumeByMouseEvent(sliderEl: HTMLDivElement, event: MouseEvent): void {
    const rect = sliderEl.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = Math.min(Math.max(clickX / rect.width, 0), 1);
    this.volume.set(percentage);
  }

  protected handleVolumeSliderClick(sliderEl: HTMLDivElement, event: MouseEvent): void {
    this.setVolumeByMouseEvent(sliderEl, event);

    const mouseup$ = fromEvent(this.document, 'mouseup').pipe(takeUntilDestroyed(this.destroyRef));

    fromEvent<MouseEvent>(this.document, 'mousemove')
      .pipe(
        tap((event) => this.setVolumeByMouseEvent(sliderEl, event)),
        takeUntil(mouseup$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected screenshot(): void {
    const videoEl = this.videoElRef()?.nativeElement;
    const deviceId = this.deviceId;

    if (!videoEl) {
      return;
    }

    const canvas = this.document.createElement('canvas');
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    canvas.getContext('2d')?.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

    this.uploading.set(true);
    canvasToBlob(canvas, 'image/png')
      .pipe(
        concatMap((blob) =>
          this.fileStorageApi.uploadPhoto({
            file: blob,
            filename: `${deviceId}_${Date.now()}.png`,
            date: new Date(),
            deviceId,
          }),
        ),
        finalize(() => this.uploading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected record(): void {
    if (this.mediaRecorder) {
      this.recordActive.set(false);
      this.mediaRecorder.stop();
      this.recordFinished$.next();
      delete this.mediaRecorder;
    } else {
      const mediaRecorder = this.createRecord();

      if (mediaRecorder) {
        this.recordActive.set(true);
        this.recordStartTime = Date.now();
        mediaRecorder.start();
        this.mediaRecorder = mediaRecorder;

        this.recordTimer.set(0);
        interval(1000)
          .pipe(
            tap(() => this.recordTimer.update((value) => value + 1)),
            takeUntil(this.recordFinished$),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    }
  }

  protected createRecord(): MediaRecorder | null {
    if (!this.videoConsumer && !this.audioConsumer) {
      return null;
    }

    const startTime = new Date();
    const deviceId = this.deviceId;
    const tracks = [this.videoConsumer?.track, this.audioConsumer?.track, this.audioProducer?.track].filter(
      (i): i is NonNullable<typeof i> => !!i,
    );

    return createRecord(tracks, async (blob: Blob) => {
      this.uploading.set(true);

      this.fileStorageApi
        .uploadVideo({
          file: blob,
          filename: `${deviceId}_${Date.now()}.webm`,
          date: startTime,
          deviceId,
        })
        .pipe(finalize(() => this.uploading.set(false)))
        .subscribe();
    });
  }
}
